import {
  Autocomplete as MuiAutocomplete,
  autocompleteClasses,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  TextField,
  CircularProgress,
} from '@mui/material';
import React, {
  forwardRef,
  MutableRefObject,
  Ref,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { Hospital } from '../../../../../shared/api/hscan/hospital';
import { useHospitals } from '../../../../../shared/hooks/hospitalHook';
import hospitalIcon from '../../../assets/icons/icon_hospital.svg';
import searchIcon from '../../../assets/icons/icon_search.svg';
import { slotInputStyle } from '../../styledComponentStyle';
import { ColoredIcon } from '../Icon';

import { SlotWrapper, SlotWrapperProps } from './SlotWrapper';
import { useEnrollSlotRef } from './hooks';
import { SlotRef } from './types';

const Autocomplete = styled(MuiAutocomplete)<
  AutocompleteProps<Hospital, false, false, false, 'div'> & {
    $fullWidth?: boolean;
  }
>`
  ${slotInputStyle}

  &.${autocompleteClasses.root} .${autocompleteClasses.inputRoot} {
    padding: 7px;
  }
  &.${autocompleteClasses.root}
    .${autocompleteClasses.inputRoot}
    .${autocompleteClasses.input} {
    padding: 0;
  }
`;

interface HospitalAutocompleteSlotProps extends SlotWrapperProps {
  value?: Hospital | null;
  onChangeQuery?(query: string): void;
  onChangeHospital?(hospital: Hospital | null): void;
  loadEnabledOnly?: boolean;
  storeEnabledOnly?: boolean;
  exclusiveHospitalIds?: string[];
}
export const HospitalAutocompleteSlot = forwardRef(
  (
    {
      value: hospitalValue,
      onChangeQuery,
      onChangeHospital,
      loadEnabledOnly,
      storeEnabledOnly,
      exclusiveHospitalIds,
      ...slotWrapperProps
    }: HospitalAutocompleteSlotProps,
    ref: Ref<SlotRef>,
  ) => {
    const wrapperRef = useRef(null);
    const autocompleteRef = useRef<HTMLInputElement | null>(null);
    useEnrollSlotRef({ ref, wrapperRef, inputs: [autocompleteRef.current] });

    const [searchTerm, setSearchTerm] = useState('');

    const { data: hospitals = [], isPending: isPendingHospitals } =
      useHospitals(searchTerm, {
        enabled: searchTerm.length > 0,
      });

    const filteredHospitals = useMemo(
      () =>
        hospitals.filter(
          hospital =>
            !exclusiveHospitalIds?.includes(hospital.id) &&
            (loadEnabledOnly
              ? hospital.loadEnabled
              : storeEnabledOnly
              ? hospital.storeEnabled
              : true),
        ),
      [hospitals, loadEnabledOnly, exclusiveHospitalIds, storeEnabledOnly],
    );

    return (
      <SlotWrapper
        {...slotWrapperProps}
        wrap
        icon={
          <ColoredIcon
            width="24px"
            height="24px"
            imageUrl={hospitalIcon}
            imageWidth="24px"
            color={'var(--HScan_Light_Blue, #2e5be0)'}
          />
        }>
        <Autocomplete
          noOptionsText={isPendingHospitals && <CircularProgress size={16} />}
          renderInput={params => (
            <AutocompleteInput
              {...params}
              autocompleteRef={autocompleteRef}
              searchTerm={searchTerm}
              onChangeSearchTerm={query => {
                setSearchTerm(query);
                onChangeQuery?.(query);
              }}
            />
          )}
          popupIcon={null}
          getOptionLabel={hospital => hospital.name}
          options={filteredHospitals}
          value={hospitalValue}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_event, value) => onChangeHospital?.(value)}
        />
      </SlotWrapper>
    );
  },
);

type InputHTMLAttributesWithRef =
  React.InputHTMLAttributes<HTMLInputElement> & {
    ref: MutableRefObject<HTMLInputElement>;
  };
type AutocompleteInputProps = AutocompleteRenderInputParams & {
  autocompleteRef: MutableRefObject<HTMLInputElement | null>;
  searchTerm: string;
  onChangeSearchTerm(value: string): void;
};
function AutocompleteInput({
  autocompleteRef,
  searchTerm,
  onChangeSearchTerm,
  ...params
}: AutocompleteInputProps) {
  const muiAutocompleteInputRef = (
    params.inputProps as InputHTMLAttributesWithRef
  ).ref;
  const callbackRef = useCallback(
    (inputRef: HTMLInputElement) => {
      muiAutocompleteInputRef.current = inputRef;
      autocompleteRef.current = inputRef;
    },
    [autocompleteRef, muiAutocompleteInputRef],
  );
  const { t } = useTranslation('translation', {
    keyPrefix: 'common.autoHospitalTextfield',
  });

  return (
    <TextField
      {...params}
      value={searchTerm}
      onChange={e => onChangeSearchTerm(e.target.value)}
      inputRef={callbackRef}
      InputProps={{
        ...params.InputProps,
        startAdornment: (
          <ColoredIcon
            width="16px"
            height="16px"
            imageUrl={searchIcon}
            imageWidth="16px"
            color="#2e5be0"
            margin="0 5px 0 0"
          />
        ),
      }}
      placeholder={`${t('placeholder')}`}
    />
  );
}
